import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { describeApiError } from '@/lib/describeApiError';
import {
  DialogContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  FormControlLabel,
  Switch,
  Grid,
  InputAdornment,
} from '@mui/material';
import { FormDialog } from '@/components/common/FormDialog';
import {
  useCreateLift,
  useUpdateLift,
  type Lift,
  type LiftType,
  type MaintenanceStatus,
} from '@/hooks';
import { useNotify } from '@/hooks/useNotify';
import { LIFT_TYPE_OPTIONS } from '@/utils/liftLabels';
import { NumberField } from '@/components/common/NumberField';

interface LiftFormDialogProps {
  open: boolean;
  lift?: Lift | null;
  onClose: () => void;
}

interface FormData {
  name: string;
  identifier: string;
  type: LiftType;
  capacityKg: number;
  lengthCm: number;
  widthCm: number;
  maxHeightCm: number;
  workshopLocation: string;
  maintenanceStatus: MaintenanceStatus;
  notes: string;
  isActive: boolean;
}

const initialFormData: FormData = {
  name: '',
  identifier: '',
  type: '2_post',
  capacityKg: 3000,
  lengthCm: 500,
  widthCm: 250,
  maxHeightCm: 200,
  workshopLocation: '',
  maintenanceStatus: 'operational',
  notes: '',
  isActive: true,
};

export function LiftFormDialog({ open, lift, onClose }: LiftFormDialogProps) {
  const { t } = useTranslation('equipment');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const isEditing = !!lift;

  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [isDirty, setIsDirty] = useState(false);

  const createMutation = useCreateLift();
  const updateMutation = useUpdateLift();

  useEffect(() => {
    if (lift) {
      setFormData({
        name: lift.name,
        identifier: lift.identifier,
        type: lift.type,
        capacityKg: lift.capacityKg,
        lengthCm: lift.lengthCm,
        widthCm: lift.widthCm,
        maxHeightCm: lift.maxHeightCm,
        workshopLocation: lift.workshopLocation ?? '',
        maintenanceStatus: lift.maintenanceStatus,
        notes: lift.notes ?? '',
        isActive: lift.isActive,
      });
    } else {
      setFormData(initialFormData);
    }
    setErrors({});
    setIsDirty(false);
  }, [lift, open]);

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('liftForm.nameRequired');
    } else if (formData.name.length > 100) {
      newErrors.name = t('liftForm.nameMaxLength');
    }

    if (!formData.identifier.trim()) {
      newErrors.identifier = t('liftForm.identifierRequired');
    } else if (formData.identifier.length > 50) {
      newErrors.identifier = t('liftForm.identifierMaxLength');
    }

    if (formData.capacityKg <= 0) {
      newErrors.capacityKg = t('liftForm.capacityPositive');
    }

    if (formData.lengthCm <= 0) {
      newErrors.lengthCm = t('liftForm.lengthPositive');
    }

    if (formData.widthCm <= 0) {
      newErrors.widthCm = t('liftForm.widthPositive');
    }

    if (formData.maxHeightCm <= 0) {
      newErrors.maxHeightCm = t('liftForm.heightPositive');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      if (lift) {
        await updateMutation.mutateAsync({
          id: lift.id,
          data: {
            name: formData.name,
            identifier: formData.identifier,
            type: formData.type,
            capacityKg: formData.capacityKg,
            lengthCm: formData.lengthCm,
            widthCm: formData.widthCm,
            maxHeightCm: formData.maxHeightCm,
            workshopLocation: formData.workshopLocation || null,
            maintenanceStatus: formData.maintenanceStatus,
            notes: formData.notes || null,
            isActive: formData.isActive,
          },
        });
        notify.success(t('liftForm.updated'), t('liftForm.updatedDesc', { name: formData.name }));
      } else {
        await createMutation.mutateAsync({
          name: formData.name,
          identifier: formData.identifier,
          type: formData.type,
          capacityKg: formData.capacityKg,
          lengthCm: formData.lengthCm,
          widthCm: formData.widthCm,
          maxHeightCm: formData.maxHeightCm,
          workshopLocation: formData.workshopLocation || undefined,
          maintenanceStatus: formData.maintenanceStatus,
          notes: formData.notes || undefined,
          isActive: formData.isActive,
        });
        notify.success(t('liftForm.created'), t('liftForm.createdDesc', { name: formData.name }));
      }
      onClose();
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('liftForm.saveFailed') }));
    }
  };

  const handleChange = (field: keyof FormData, value: string | number | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={isEditing ? t('liftForm.editTitle') : t('liftForm.createTitle')}
      isDirty={isDirty}
      onSubmit={() => void handleSubmit()}
      isLoading={isPending}
      submitLabel={isEditing ? tc('save') : tc('add')}
      maxWidth="md"
    >
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label={t('lifts.name')}
                value={formData.name}
                onChange={(e) => { handleChange('name', e.target.value); }}
                error={!!errors.name}
                helperText={errors.name ?? t('liftForm.nameHint')}
                fullWidth
                required
                autoFocus
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label={t('lifts.identifier')}
                value={formData.identifier}
                onChange={(e) => { handleChange('identifier', e.target.value); }}
                error={!!errors.identifier}
                helperText={errors.identifier ?? t('liftForm.identifierHint')}
                fullWidth
                required
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>{t('lifts.type')}</InputLabel>
                <Select
                  value={formData.type}
                  label={t('lifts.type')}
                  onChange={(e) => { handleChange('type', e.target.value as LiftType); }}
                >
                  {LIFT_TYPE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{t(opt.labelKey)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label={t('lifts.location')}
                value={formData.workshopLocation}
                onChange={(e) => { handleChange('workshopLocation', e.target.value); }}
                fullWidth
                placeholder={t('liftForm.locationPlaceholder')}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <NumberField
                label={t('lifts.capacity')}
                value={formData.capacityKg}
                min={0}
                onChange={(val) => { handleChange('capacityKg', val ?? 0); }}
                error={!!errors.capacityKg}
                helperText={errors.capacityKg}
                fullWidth
                required
                slotProps={{
                  input: {
                    endAdornment: <InputAdornment position="end">kg</InputAdornment>,
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <NumberField
                label={t('liftForm.maxHeight')}
                value={formData.maxHeightCm}
                min={0}
                onChange={(val) => { handleChange('maxHeightCm', val ?? 0); }}
                error={!!errors.maxHeightCm}
                helperText={errors.maxHeightCm}
                fullWidth
                required
                slotProps={{
                  input: {
                    endAdornment: <InputAdornment position="end">cm</InputAdornment>,
                  },
                }}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <NumberField
                label={t('liftForm.platformLength')}
                value={formData.lengthCm}
                min={0}
                onChange={(val) => { handleChange('lengthCm', val ?? 0); }}
                error={!!errors.lengthCm}
                helperText={errors.lengthCm ?? t('liftForm.maxVehicleLength')}
                fullWidth
                required
                slotProps={{
                  input: {
                    endAdornment: <InputAdornment position="end">cm</InputAdornment>,
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <NumberField
                label={t('liftForm.platformWidth')}
                value={formData.widthCm}
                min={0}
                onChange={(val) => { handleChange('widthCm', val ?? 0); }}
                error={!!errors.widthCm}
                helperText={errors.widthCm ?? t('liftForm.maxVehicleWidth')}
                fullWidth
                required
                slotProps={{
                  input: {
                    endAdornment: <InputAdornment position="end">cm</InputAdornment>,
                  },
                }}
              />
            </Grid>
          </Grid>

          <TextField
            label={t('liftForm.notes')}
            value={formData.notes}
            onChange={(e) => { handleChange('notes', e.target.value); }}
            fullWidth
            multiline
            rows={2}
            placeholder={t('liftForm.notesPlaceholder')}
          />

          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={(e) => { handleChange('isActive', e.target.checked); }}
              />
            }
            label={tc('active')}
          />
        </Stack>
      </DialogContent>
    </FormDialog>
  );
}
